<!--
  Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
  This information is confidential and proprietary to GM Global Technology
  Operations LLC and may not be used, modified, copied or distributed.
  -->
<br/>
<h1 class="page-header">Template Editor</h1>
<br/>
@if (permission()) {
<table style="width:100%">
  <tr>
    <td><h3 style="font-size: x-large; font-weight: bold">{{getPrefix()}} Timing Templates</h3></td>
    <td></td>
    <p-confirmDialog key="templatePublishConfirmation"></p-confirmDialog>
    <p-confirmDialog key="milestoneUpdateConfirmation"></p-confirmDialog>
    <p-dialog header="New Template Version Form" [(visible)]="showNewVersionModal"
      styleClass="kts-version-modal" [style]="{width: '45vw', height: '28vw'}">
      <p>Which released version would you like to copy from?</p>
      <div class="buttons" style="float: left;" >
        <p-floatlabel class="mr-2 version-select-field">
          <p-select appendTo="body" [options]="currentTemplateVersions()" [(ngModel)]="selectedVersionToCopy"
            id="copy-from" inputId="copy-from">
          </p-select>
          <label for="copy-from">Copy From</label>
        </p-floatlabel>
        @if (selectedVersionToCopy) {
          @if (allowsVersionPrefixes) {
            <p-floatlabel class="mr-2 designator-field">
              <p-select appendTo="body" [options]="versionPrefixes" id="version-prefix" inputId="version-prefix"
                [(ngModel)]="newVersionPrefix">
              </p-select>
              <label for="version-prefix">Designator</label>
            </p-floatlabel>
          }
          @if (allowsNewMajorVersion) {
            <p-floatlabel class="mr-2">
              <p-inputNumber [(ngModel)]="newTemplateVersion" mode="decimal" id="new-version"
                inputId="new-version" [minFractionDigits]="1" [maxFractionDigits]="1"></p-inputNumber>
              <label for="new-version">New Version</label>
            </p-floatlabel>
          }
        }
        <p-button label="Create Draft" (click)="createNewTemplate()" [disabled]="!newTemplateVersion"
        [severity]="!newTemplateVersion ? 'secondary' : null"
        icon="pi pi-save" class="gm-btn-primary mr-2"></p-button>
      </div>
    </p-dialog>
    <p-dialog header="Modify Released Version Form" [(visible)]="showNewRevisionModal"
      styleClass="kts-version-modal" [style]="{width: '45vw', height: '28vw'}">
      <p>Which released version would you like to modify?<br/></p>
      <div class="buttons" style="float: left;" >
        <p-floatlabel class="mr-2 version-select-field">
          <p-select appendTo="body" [options]="currentTemplateVersions()" [(ngModel)]="selectedVersionToCopy"
            id="version-to-modify" inputId="version-to-modify">
          </p-select>
          <label for="version-to-modify">Version to Modify</label>
        </p-floatlabel>
        <p-button label="Start Editing" (click)="createNewTemplate()" [disabled]="!selectedVersionToCopy"
        [severity]="!selectedVersionToCopy ? 'secondary' : null"
        icon="pi pi-save" class="gm-btn-primary mr-2"></p-button>
      </div>
    </p-dialog>
    <td>
      <div class="buttons" style="float: right;">
        <p-select [options]="types" [(ngModel)]="type" (ngModelChange)="onTypeChange()"
          [disabled]="saving || exporting || resetting"
          placeholder="Template Type" class="mr-2">
        </p-select>
        @if (version) {
          @if (isCurrentVersionDraft()) {
            <p-button icon="pi pi-upload" [label]="'Publish Draft ' + KtsTimingTemplateRow.getMajorVersionFromFullVersion(version)"
              [disabled]="!isCurrentVersionDraft()"
            (click)="saveTimingTemplates('ACTIVE')" class="gm-btn-primary mr-2"></p-button>
          }
        }
        <p-button label="Save All Changes" (click)="saveTimingTemplates()" icon="pi pi-save"
          [disabled]="saveAllDisabled()"
          [severity]="saveAllSeverity()"
        [loading]="!!(saving || exporting || resetting)" class="gm-btn-primary mr-2"></p-button>
        <p-button label="Reset Changes" (click)="resetChanges()" [loading]="!!(saving || exporting || resetting)"
        severity="warn" class="mr-2" icon="pi pi-replay"></p-button>
        @if (isCurrentVersionActive()) {
          <p-button label="Change Released Version" (click)="showNewRevisionModal = true; selectedVersionToCopy = KtsTimingTemplateRow.getMajorVersionFromFullVersion(version)"
          [loading]="!!(saving || exporting || resetting)" class="gm-btn-primary mr-2" icon="pi pi-pencil"></p-button>
        }
        @if (allowsNewMajorVersion) {
          <p-button label="New Version" (click)="showNewVersionModal = true; selectedVersionToCopy = KtsTimingTemplateRow.getMajorVersionFromFullVersion(version)"
          [loading]="!!(saving || exporting || resetting)" class="gm-btn-primary mr-2" icon="pi pi-plus"></p-button>
        }
        <p-splitbutton icon="pi pi-download" label="Export Template Versions" [disabled]="saving || exporting || resetting"
        [severity]="(saving || exporting || resetting) ? 'secondary' : null"
        [model]="downloadTemplateButtonOptions.get(this.type)" appendTo="body"
        [menuStyle]="{ 'max-height': '50vh', 'overflow-y': 'auto', 'min-width': '240px' }"
        menuStyleClass="kts-scrollable-menu" class="mr-2"></p-splitbutton>
        @if ((deleteDraftVersionOptions.get(this.type)?.length ?? 0) > 0) {
          <p-splitbutton icon="pi pi-trash" [disabled]="!deleteDraftVersionOptions.get(this.type)?.length"
            [severity]="!deleteDraftVersionOptions.get(this.type)?.length ? 'secondary' : null"
            label="Delete Drafts" class="mr-2" appendTo="body"
            [menuStyle]="{ 'max-height': '50vh', 'overflow-y': 'auto', 'min-width': '240px' }"
          [model]="deleteDraftVersionOptions.get(this.type)" menuStyleClass="kts-scrollable-menu"></p-splitbutton>
        }
      </div>
    </td>
  </tr>
</table>
<p-confirmDialog key="deleteRowConfirmation" esciConfirmEnterAccept defaultFocus="accept"></p-confirmDialog>
@if (permission() && hasTabs) {
  <p-tabs [value]="safeActiveTabIndex" (valueChange)="activeTabIndex = +$event">
    <p-tablist>
      @for (entry of fullCols.get(type) | keyvalue; track entry; let i = $index) {
        <p-tab [value]="i" (click)="handleTabClick(entry.key)">
          <span>
            <i class="pi pi-calendar"></i>
            Version {{KtsTimingTemplateRow.getMajorVersionFromFullVersion(entry.key)}} {{getStatusOfVersionForLabel(entry.key, type)}}
          </span>
        </p-tab>
      }
    </p-tablist>
    <p-tabpanels>
      @for (entry of fullCols.get(type) | keyvalue; track entry; let i = $index) {
        <p-tabpanel [value]="i">
          <p>
            <ag-grid-angular style="width: 100%; height: 80vh;" class="ag-theme-balham" [rowHeight]="30"
              [undoRedoCellEditing]="true" [enableCellTextSelection]="true"
              (cellEditingStopped)="cellEditingStopped($event)"
              (dragStarted)="window.getSelection()?.removeAllRanges()"
              [undoRedoCellEditingLimit]="50" (dragStopped)="onRowDragStopped($event, entry.key)"
              [columnDefs]="fullCols.get(type)?.get(entry.key)" [rowData]="ktsTimingTemplateRowMap.get(type)?.get(entry.key)"
              [autoSizeStrategy]="autoSizeStrategy" [rowClassRules]="rowClassRules"
              [rowDragManaged]="true" [defaultColDef]="defaultColDef"
            (gridReady)="onGridReady($event, entry.key)"></ag-grid-angular>
          </p>
        </p-tabpanel>
      }
    </p-tabpanels>
  </p-tabs>
}
}
